import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { compressImagesIfNeeded } from '../../utils/imageCompression';

/** Página que o fornecedor abre. Nada aqui exige login: o token do link é a chave. */

interface CampoCotacao {
  id: string;
  nome: string;
  tipo: string;
  opcoes?: string[];
  obrigatorio?: boolean;
  valor?: any;
  /** De qual etapa do fluxo o campo veio — serve para agrupar o contexto. */
  etapaId?: string;
  etapaNome?: string;
}

interface PortalCotacaoProps {
  token: string;
  apiBase: string;
  dados: {
    portal: { nome?: string; fornecedor?: { nome?: string }; jaRespondida?: boolean };
    card: { titulo?: string; protocolo?: string };
    contexto: CampoCotacao[];
    campos: CampoCotacao[];
  };
}

interface ArquivoTemp {
  localId: string;
  status: 'enviando' | 'pronto' | 'erro';
  name: string;
  size: number;
  type: string;
  tempId?: string;
  erro?: string;
  /** Miniatura local (blob:) das imagens, para conferir o print antes de enviar. */
  previewUrl?: string;
  /** Comentário que o fornecedor escreve embaixo do print. */
  descricao?: string;
}

/** Teto de arquivos por campo: folga para vários prints sem virar depósito. */
const MAX_ARQUIVOS_POR_CAMPO = 20;

const ehArquivoImagem = (a: { type?: string; name?: string }) =>
  (a.type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(a.name || '');

/** Print colado vem como "image.png" (e a câmera do iPhone como "image.jpg"):
 *  dá um nome com data/hora para não virar uma pilha de arquivos iguais. */
const nomearSeGenerico = (f: File, i: number): File => {
  if (f.name && !/^image\.\w+$/i.test(f.name)) return f;
  const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
  const d = new Date();
  const dois = (n: number) => String(n).padStart(2, '0');
  const carimbo = `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}_${dois(d.getHours())}-${dois(d.getMinutes())}-${dois(d.getSeconds())}`;
  return new File([f], `print-${carimbo}-${i + 1}.${ext}`, { type: f.type, lastModified: f.lastModified });
};

const mostrarValor = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (Array.isArray(v)) {
    if (!v.length) return '—';
    if (typeof v[0] === 'object') {
      // viajantes (Viagens, 29/10/2026): nome e CPF de cada um
      if (v.some((x: any) => x && 'cpf' in x && 'nome' in x)) {
        return v.filter((x: any) => x && (x.nome || x.cpf)).map((x: any) => `${x.nome || '?'} (CPF ${x.cpf || '—'})`).join('; ');
      }
      // trechos de roteiro: "FLN → GRU 30/12/2026" (antes saía "2 arquivo(s)")
      if (v.some((x: any) => x && ('origem' in x || 'destino' in x))) {
        const dt = (d: any) => { const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? ` ${m[3]}/${m[2]}/${m[1]}` : ''; };
        return v.filter((x: any) => x && (x.origem || x.destino)).map((x: any) => `${x.origem || '?'} → ${x.destino || '?'}${dt(x.data)}`).join(' | ');
      }
      // lista de anexos: o nome do arquivo diz mais que "3 arquivo(s)"
      const nomes = v.map((x: any) => x?.name || x?.originalName).filter(Boolean);
      return nomes.length ? nomes.join(', ') : `${v.length} arquivo(s)`;
    }
    return v.join(', ');
  }
  if (typeof v === 'object') {
    const e = Object.entries(v).filter(([, x]) => x !== '' && x !== null && x !== undefined);
    return e.length ? e.map(([k, x]) => `${k}: ${mostrarValor(x)}`).join(' · ') : '—';
  }
  return String(v);
};

const ehAnexo = (tipo: string) => tipo === 'anexo' || tipo === 'arquivo';
const ehMultiEscolha = (c: CampoCotacao) =>
  c.tipo === 'checkbox_multi' || (c.tipo === 'checkbox' && !!c.opcoes?.length);
const ehLista = (c: CampoCotacao) =>
  ['lista', 'select', 'selecao', 'checkbox_single'].includes(c.tipo) && !!c.opcoes?.length;

const entrada =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent';

export default function PortalCotacao({ token, apiBase, dados }: PortalCotacaoProps) {
  const [valores, setValores] = useState<Record<string, any>>({});
  const [arquivos, setArquivos] = useState<Record<string, ArquivoTemp[]>>({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState('');
  // Campo de anexo que recebe o Ctrl+V: o último em que a pessoa mexeu
  const [campoColar, setCampoColar] = useState<string | null>(null);
  const [arrastandoEm, setArrastandoEm] = useState<string | null>(null);
  const [ampliado, setAmpliado] = useState<{ url: string; nome: string } | null>(null);
  // Prints com a caixa de comentário aberta (abre sozinha quando já tem texto)
  const [comentando, setComentando] = useState<Record<string, boolean>>({});
  // Arquivo original de cada envio, para "tentar de novo" sem escolher outra vez
  const arquivosOriginais = useRef<Record<string, File>>({});

  const fornecedor = dados.portal?.fornecedor?.nome || '';
  // "+ Adicionar cotação": cada cotação a mais repete os campos da proposta com
  // id próprio (x2__<campo>, x3__<campo>…); no envio vai em cotacoesExtras.
  const [qtdExtras, setQtdExtras] = useState(0);
  const idExtra = (n: number, campoId: string) => `x${n}__${campoId}`;
  const rotuloExtra = (n: number) => {
    const base = (dados.campos || []).find(c => ehAnexo(c.tipo))?.nome || 'Cotação 01';
    const nn = String(n).padStart(2, '0');
    return /0?1\b/.test(base) ? base.replace(/0?1\b/, nn) : `${base} (${nn})`;
  };
  const camposExtra = (n: number): CampoCotacao[] =>
    (dados.campos || []).map(c => ({
      ...c,
      id: idExtra(n, c.id),
      nome: ehAnexo(c.tipo) ? rotuloExtra(n) : c.nome,
      obrigatorio: false,
    }));
  const camposAnexo = [
    ...(dados.campos || []),
    ...Array.from({ length: qtdExtras }, (_, i) => camposExtra(i + 2)).flat(),
  ].filter(c => ehAnexo(c.tipo));
  const campoDoColar = campoColar || camposAnexo[0]?.id || null;

  // O contexto vem de várias etapas do fluxo; agrupar deixa claro de onde
  // saiu cada informação (a solicitação, a aprovação, a etapa da cotação).
  const gruposContexto = useMemo(() => {
    const grupos: { nome: string; campos: CampoCotacao[] }[] = [];
    (dados.contexto || []).forEach(campo => {
      const nome = campo.etapaNome || '';
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.nome === nome) ultimo.campos.push(campo);
      else grupos.push({ nome, campos: [campo] });
    });
    return grupos;
  }, [dados.contexto]);

  const preenchido = (c: CampoCotacao) => {
    if (ehAnexo(c.tipo)) return (arquivos[c.id] || []).some(a => a.status === 'pronto');
    const v = valores[c.id];
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && v !== '';
  };

  const faltando = (dados.campos || []).filter(c => c.obrigatorio && !preenchido(c));
  const subindo = Object.values(arquivos).flat().some(a => a.status === 'enviando');

  const setValor = (id: string, v: any) => setValores(prev => ({ ...prev, [id]: v }));

  const alternarOpcao = (id: string, opcao: string) => {
    setValores(prev => {
      const atual: string[] = Array.isArray(prev[id]) ? prev[id] : [];
      return {
        ...prev,
        [id]: atual.includes(opcao) ? atual.filter(o => o !== opcao) : [...atual, opcao],
      };
    });
  };

  /** Sobe o arquivo na hora: se a aba cair, o que já subiu não se perde.
   *  Com `localIdExistente`, é uma nova tentativa de um envio que falhou. */
  const enviarArquivo = async (campoId: string, file: File, localIdExistente?: string) => {
    const localId = localIdExistente || `l_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    arquivosOriginais.current[localId] = file;

    const atualizar = (mudanca: Partial<ArquivoTemp>) =>
      setArquivos(prev => ({
        ...prev,
        [campoId]: (prev[campoId] || []).map(a => (a.localId === localId ? { ...a, ...mudanca } : a)),
      }));

    if (localIdExistente) {
      atualizar({ status: 'enviando', erro: undefined });
    } else {
      const item: ArquivoTemp = {
        localId, status: 'enviando', name: file.name, size: file.size, type: file.type,
        previewUrl: ehArquivoImagem(file) ? URL.createObjectURL(file) : undefined,
      };
      setArquivos(prev => ({ ...prev, [campoId]: [...(prev[campoId] || []), item] }));
    }

    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await fetch(`${apiBase}/api/portal-publico/${token}/upload-temp`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Falha no envio');
      atualizar({ status: 'pronto', tempId: data.tempId, size: data.size || file.size });
    } catch (e: any) {
      atualizar({ status: 'erro', erro: e?.message || 'Falha no envio' });
    }
  };

  const removerArquivo = (campoId: string, localId: string) => {
    const item = (arquivos[campoId] || []).find(a => a.localId === localId);
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
    delete arquivosOriginais.current[localId];
    setArquivos(prev => ({
      ...prev,
      [campoId]: (prev[campoId] || []).filter(a => a.localId !== localId),
    }));
  };

  const comentar = (campoId: string, localId: string, descricao: string) =>
    setArquivos(prev => ({
      ...prev,
      [campoId]: (prev[campoId] || []).map(a => (a.localId === localId ? { ...a, descricao } : a)),
    }));

  const tentarDeNovo = (campoId: string, localId: string) => {
    const file = arquivosOriginais.current[localId];
    if (file) enviarArquivo(campoId, file, localId);
  };

  /** Entrada única dos arquivos: galeria/câmera, seletor, arrastar ou Ctrl+V. */
  const adicionarArquivos = async (campoId: string, novos: File[]) => {
    if (novos.length === 0) return;
    setCampoColar(campoId);
    const espaco = MAX_ARQUIVOS_POR_CAMPO - (arquivos[campoId] || []).length;
    if (espaco <= 0) {
      setErro(`Limite de ${MAX_ARQUIVOS_POR_CAMPO} arquivos neste campo. Remova algum para adicionar outro.`);
      return;
    }
    if (novos.length > espaco) {
      setErro(`Só cabem mais ${espaco} arquivo(s) neste campo; os demais ficaram de fora.`);
    } else {
      setErro('');
    }
    // foto grande de celular é reduzida antes de subir (rede móvel e limite por arquivo)
    const prontos = await compressImagesIfNeeded(novos.slice(0, espaco).map(nomearSeGenerico));
    prontos.forEach(f => enviarArquivo(campoId, f));
  };

  // Ctrl+V em qualquer ponto da página cola o print no campo de anexo ativo.
  // O listener é único; a ref aponta sempre para a versão atual da função.
  const colarRef = useRef<(e: ClipboardEvent) => void>(() => {});
  colarRef.current = (e: ClipboardEvent) => {
    if (enviado || !campoDoColar) return;
    const files = Array.from(e.clipboardData?.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter((f): f is File => !!f);
    if (files.length === 0) return;
    e.preventDefault();
    adicionarArquivos(campoDoColar, files);
  };
  useEffect(() => {
    const aoColar = (e: ClipboardEvent) => colarRef.current(e);
    window.addEventListener('paste', aoColar);
    return () => window.removeEventListener('paste', aoColar);
  }, []);

  // Libera as miniaturas locais quando a página fecha
  const arquivosRef = useRef(arquivos);
  arquivosRef.current = arquivos;
  useEffect(() => () => {
    Object.values(arquivosRef.current).flat().forEach(a => {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    });
  }, []);

  const enviar = async () => {
    if (faltando.length > 0) {
      setErro(`Preencha: ${faltando.map(c => c.nome).join(', ')}`);
      return;
    }
    if (subindo) {
      setErro('Espere os arquivos terminarem de subir.');
      return;
    }
    if (Object.values(arquivos).flat().some(a => a.status === 'erro')) {
      setErro('Algum arquivo não subiu. Toque em "tentar de novo" ou remova antes de enviar.');
      return;
    }
    const arquivosTemp: Record<string, any[]> = {};
    const camposBase: Record<string, any> = {};
    const extras: Record<number, { campos: Record<string, any>; arquivosTemp: Record<string, any[]> }> = {};
    const doExtra = (chave: string) => {
      const m = /^x(\d+)__(.+)$/.exec(chave);
      if (!m) return null;
      const n = Number(m[1]);
      extras[n] = extras[n] || { campos: {}, arquivosTemp: {} };
      return { item: extras[n], campoId: m[2] };
    };
    Object.entries(arquivos).forEach(([campoId, lista]) => {
      const prontos = lista
        .filter(a => a.status === 'pronto' && a.tempId)
        .map(a => ({
          tempId: a.tempId, originalName: a.name, size: a.size, type: a.type,
          ...(a.descricao?.trim() ? { descricao: a.descricao.trim() } : {}),
        }));
      if (!prontos.length) return;
      const ex = doExtra(campoId);
      if (ex) ex.item.arquivosTemp[ex.campoId] = prontos;
      else arquivosTemp[campoId] = prontos;
    });
    Object.entries(valores).forEach(([campoId, v]) => {
      const ex = doExtra(campoId);
      if (ex) ex.item.campos[ex.campoId] = v;
      else camposBase[campoId] = v;
    });
    const cotacoesExtras = Object.keys(extras).map(Number).sort((a, b) => a - b)
      .filter(n => n <= qtdExtras + 1)
      .map(n => ({ rotulo: rotuloExtra(n), ...extras[n] }));

    const temValor = Object.values(camposBase).some(
      v => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)
    );
    if (!temValor && Object.keys(arquivosTemp).length === 0 && cotacoesExtras.length === 0) {
      setErro('Preencha ao menos um campo');
      return;
    }

    try {
      setEnviando(true);
      setErro('');
      const res = await fetch(`${apiBase}/api/portal-publico/${token}/submeter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campos: camposBase, arquivosTemp, cotacoesExtras }),
      });
      const data = await res.json();
      if (data.success) setEnviado(true);
      else setErro(data.error || 'Não consegui enviar. Tente novamente.');
    } catch {
      setErro('Não consegui enviar. Verifique sua conexão.');
    } finally {
      setEnviando(false);
    }
  };

  if (enviado || dados.portal?.jaRespondida) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <div className="text-4xl mb-3">✅</div>
          <h1 className="text-lg font-semibold text-gray-900 mb-1">
            {enviado ? 'Cotação enviada!' : 'Esta cotação já foi respondida'}
          </h1>
          <p className="text-sm text-gray-500">
            {enviado
              ? 'Obrigado. Sua proposta foi registrada e já está com o solicitante.'
              : 'Se precisar corrigir algo, fale com quem te enviou este link.'}
          </p>
          {dados.card?.protocolo && (
            <p className="text-xs text-gray-400 mt-4">Protocolo {dados.card.protocolo}</p>
          )}
        </div>
      </div>
    );
  }

  const renderCampo = (campo: CampoCotacao) => {
    const v = valores[campo.id];

    if (ehAnexo(campo.tipo)) {
      const lista = arquivos[campo.id] || [];
      const prontos = lista.filter(a => a.status === 'pronto').length;
      const cheio = lista.length >= MAX_ARQUIVOS_POR_CAMPO;
      const ativoNoColar = campoDoColar === campo.id && camposAnexo.length > 1;
      const aoEscolher = (e: ChangeEvent<HTMLInputElement>) => {
        const novos = Array.from(e.target.files || []);
        e.target.value = '';
        adicionarArquivos(campo.id, novos);
      };
      const botao =
        'flex-1 min-w-[6.5rem] inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors cursor-pointer select-none';
      const botaoAtivo = 'border-gray-300 bg-white text-gray-700 hover:border-emerald-400 hover:bg-emerald-50 active:bg-emerald-100';
      const botaoInativo = 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed';
      return (
        <div
          onPointerDown={() => setCampoColar(campo.id)}
          onFocus={() => setCampoColar(campo.id)}
          onDragOver={e => { e.preventDefault(); setArrastandoEm(campo.id); }}
          onDragLeave={() => setArrastandoEm(null)}
          onDrop={e => {
            e.preventDefault();
            setArrastandoEm(null);
            if (!cheio) adicionarArquivos(campo.id, Array.from(e.dataTransfer.files || []));
          }}
          className={`space-y-2 rounded-xl border-2 border-dashed p-2.5 sm:p-3 transition-colors ${
            arrastandoEm === campo.id ? 'border-emerald-400 bg-emerald-50/60'
            : ativoNoColar ? 'border-emerald-200 bg-white' : 'border-gray-200 bg-white'
          }`}
        >
          {/* Miniaturas: cada print/arquivo separado, com remover */}
          {lista.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {lista.map(a => {
                const imagem = !!a.previewUrl;
                const caixaAberta = comentando[a.localId] || !!a.descricao;
                return (
                  <div key={a.localId} className="flex flex-col gap-1 min-w-0">
                  <div className="relative aspect-square">
                    <button
                      type="button"
                      onClick={() => imagem && setAmpliado({ url: a.previewUrl!, nome: a.name })}
                      title={a.name}
                      className={`w-full h-full rounded-lg border overflow-hidden flex flex-col items-center justify-center gap-1 bg-gray-50 ${
                        a.status === 'erro' ? 'border-red-300' : 'border-gray-200'
                      } ${imagem ? 'cursor-zoom-in' : 'cursor-default'}`}
                    >
                      {imagem ? (
                        <img src={a.previewUrl} alt={a.name} className="w-full h-full object-cover" draggable={false} />
                      ) : (
                        <>
                          <span className="text-2xl leading-none">{/\.pdf$/i.test(a.name) ? '📄' : '📎'}</span>
                          <span className="px-1.5 w-full text-[10px] text-gray-600 text-center truncate">{a.name}</span>
                        </>
                      )}
                    </button>

                    {a.status === 'enviando' && (
                      <div className="absolute inset-0 rounded-lg bg-white/60 flex items-center justify-center pointer-events-none">
                        <div className="w-6 h-6 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
                      </div>
                    )}
                    {a.status === 'pronto' && (
                      <span className="absolute bottom-1 left-1 w-5 h-5 rounded-full bg-emerald-600 text-white text-[11px] flex items-center justify-center shadow pointer-events-none">✓</span>
                    )}
                    {a.status === 'erro' && (
                      <button
                        type="button"
                        onClick={() => tentarDeNovo(campo.id, a.localId)}
                        title={a.erro || 'Falhou'}
                        className="absolute inset-x-1 bottom-1 rounded-md bg-red-600/90 text-white text-[10px] font-medium py-1"
                      >
                        Tentar de novo
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => removerArquivo(campo.id, a.localId)}
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-300 shadow-sm flex items-center justify-center text-base leading-none"
                      title="Remover"
                      aria-label={`Remover ${a.name}`}
                    >×</button>
                  </div>
                  {caixaAberta ? (
                    <textarea
                      rows={2}
                      autoFocus={comentando[a.localId] && !a.descricao}
                      value={a.descricao || ''}
                      maxLength={1000}
                      onChange={e => comentar(campo.id, a.localId, e.target.value)}
                      onBlur={() => !a.descricao?.trim() && setComentando(prev => ({ ...prev, [a.localId]: false }))}
                      placeholder="Escreva sobre este print…"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-y"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setComentando(prev => ({ ...prev, [a.localId]: true }))}
                      className="w-full py-1 rounded-md border border-dashed border-gray-300 text-[11px] text-gray-500 hover:border-emerald-400 hover:text-emerald-700"
                    >
                      ✏️ Escrever
                    </button>
                  )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Adicionar: no celular "Fotos" abre galeria/câmera; "Arquivos" pega PDF, planilha… */}
          <div className="flex flex-wrap gap-2">
            <label className={`${botao} ${cheio ? botaoInativo : botaoAtivo}`}>
              <span>🖼️</span> {lista.length ? 'Mais fotos' : 'Fotos/prints'}
              <input type="file" accept="image/*" multiple disabled={cheio} onChange={aoEscolher} className="sr-only" />
            </label>
            <label className={`${botao} sm:hidden ${cheio ? botaoInativo : botaoAtivo}`}>
              <span>📷</span> Câmera
              <input type="file" accept="image/*" capture="environment" disabled={cheio} onChange={aoEscolher} className="sr-only" />
            </label>
            <label className={`${botao} ${cheio ? botaoInativo : botaoAtivo}`}>
              <span>📄</span> Arquivos
              <input type="file" multiple disabled={cheio} onChange={aoEscolher} className="sr-only" />
            </label>
          </div>

          <p className="text-[11px] text-gray-400">
            {lista.length > 0 && <>{prontos}/{lista.length} enviado(s) · </>}
            {cheio
              ? `limite de ${MAX_ARQUIVOS_POR_CAMPO} arquivos`
              : <>pode mandar vários de uma vez<span className="hidden sm:inline">, arrastar para cá ou colar um print com Ctrl+V{ativoNoColar ? ' (vai para este campo)' : ''}</span></>}
          </p>
        </div>
      );
    }

    if (campo.tipo === 'texto_longo' || campo.tipo === 'textarea') {
      return (
        <textarea rows={3} value={v || ''} className={entrada}
          onChange={e => setValor(campo.id, e.target.value)} />
      );
    }

    if (ehLista(campo)) {
      return (
        <select value={v || ''} className={entrada}
          onChange={e => setValor(campo.id, e.target.value)}>
          <option value="">Selecione…</option>
          {campo.opcoes!.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }

    if (ehMultiEscolha(campo)) {
      const marcados: string[] = Array.isArray(v) ? v : [];
      return (
        <div className="space-y-1.5">
          {campo.opcoes!.map(o => (
            <label key={o} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={marcados.includes(o)}
                onChange={() => alternarOpcao(campo.id, o)}
                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              {o}
            </label>
          ))}
        </div>
      );
    }

    if (campo.tipo === 'checkbox') {
      return (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!v}
            onChange={e => setValor(campo.id, e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          Sim
        </label>
      );
    }

    if (['valor', 'moeda', 'currency'].includes(campo.tipo)) {
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">R$</span>
          <input
            type="number" step="0.01" min="0" inputMode="decimal"
            value={v ?? ''}
            onChange={e => setValor(campo.id, e.target.value)}
            className={`${entrada} pl-9`}
          />
        </div>
      );
    }

    const tipoInput =
      ['numero', 'number', 'progresso'].includes(campo.tipo) ? 'number'
      : ['data', 'date', 'data_vencimento'].includes(campo.tipo) ? 'date'
      : campo.tipo === 'telefone' ? 'tel'
      : 'text';

    return (
      <input
        type={tipoInput}
        value={v ?? ''}
        onChange={e => setValor(campo.id, e.target.value)}
        className={entrada}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-2xl mx-auto space-y-4">

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h1 className="text-lg font-semibold text-gray-900">Pedido de cotação</h1>
          <p className="text-sm text-gray-500 mt-0.5">{dados.card?.titulo}</p>
          {fornecedor && (
            <p className="text-xs text-gray-400 mt-2">Este link é exclusivo de <b>{fornecedor}</b>.</p>
          )}
        </div>

        {/* O que ele precisa saber para cotar */}
        {gruposContexto.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 bg-gray-50/70 border-b border-gray-200">
              <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Detalhes do pedido</h2>
            </div>
            <div className="p-4 sm:p-6 space-y-5">
              {gruposContexto.map((grupo, i) => (
                <div key={`${grupo.nome}-${i}`} className="space-y-3">
                  {grupo.nome && gruposContexto.length > 1 && (
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      {grupo.nome}
                    </p>
                  )}
                  {grupo.campos.map(c => (
                    <div key={c.id} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
                      <span className="text-xs text-gray-500 sm:w-44 shrink-0">{c.nome}</span>
                      <span className="text-sm text-gray-800 break-words">{mostrarValor(c.valor)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* O que ele preenche */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 bg-emerald-50/60 border-b border-emerald-100">
            <h2 className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Sua proposta</h2>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            {dados.campos.map(campo => (
              <div key={campo.id}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {campo.nome}
                  {campo.obrigatorio && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                {renderCampo(campo)}
              </div>
            ))}

            {/* Cotações a mais: mesmos campos, outro conjunto de prints/valor */}
            {Array.from({ length: qtdExtras }, (_, i) => i + 2).map(n => (
              <div key={n} className="pt-4 border-t border-dashed border-emerald-200 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">{rotuloExtra(n)}</p>
                  {n === qtdExtras + 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setArquivos(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(`x${n}__`))));
                        setValores(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(`x${n}__`))));
                        setCampoColar(null);
                        setQtdExtras(q => q - 1);
                      }}
                      className="text-xs text-gray-400 hover:text-red-600"
                    >Remover</button>
                  )}
                </div>
                {camposExtra(n).map(campo => (
                  <div key={campo.id}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{campo.nome}</label>
                    {renderCampo(campo)}
                  </div>
                ))}
              </div>
            ))}

            {camposAnexo.length > 0 && qtdExtras < 19 && (
              <button
                type="button"
                onClick={() => { setQtdExtras(q => q + 1); setCampoColar(idExtra(qtdExtras + 2, camposAnexo[0].id)); }}
                className="w-full py-2.5 rounded-lg border-2 border-dashed border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50"
              >
                + Adicionar cotação ({rotuloExtra(qtdExtras + 2)})
              </button>
            )}

            {erro && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2.5">{erro}</p>
            )}

            <button
              onClick={enviar}
              disabled={enviando || subindo}
              className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {enviando ? 'Enviando…' : subindo ? 'Aguardando anexos…' : 'Enviar proposta'}
            </button>
            <p className="text-[11px] text-gray-400 text-center">
              Você só pode enviar uma vez. Confira antes de mandar.
            </p>
          </div>
        </div>
      </div>

      {/* Print em tamanho grande, para conferir antes de enviar */}
      {ampliado && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col"
          onClick={() => setAmpliado(null)}
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 text-white">
            <p className="text-sm truncate">{ampliado.nome}</p>
            <button
              type="button"
              className="w-9 h-9 shrink-0 rounded-full bg-white/10 hover:bg-white/20 text-xl leading-none"
              aria-label="Fechar"
            >×</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-2 sm:p-6 overflow-hidden">
            <img src={ampliado.url} alt={ampliado.nome} className="max-w-full max-h-full object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}
